import * as React from 'react';
import { ControlPlaneClientManager } from '../clients/ControlPlaneClientManager';
import { PermissionClaim } from '../code-generated/PermissionClaim.generated';
import { LegacyIamRole, useLegacyIamRoleCredentials } from './LegacyIamRoles';
import { useAppSettings } from './AppSettings';
import { ControlPlaneClientManagerContext } from './ControlPlaneClientManager';
import { useUsername } from './Midway';
import { NOT_LOADED, useCredentialsForPermissionClaim } from './Permissions';

/** Used to create and provide a ControlPlaneClientManager to the application */
export function ControlPlaneClientManagerProvider(props: { readonly children: React.ReactNode }) {
  const username = useUsername();
  const { region, stage } = useAppSettings();
  const gsocReadonlyCredentials = useLegacyIamRoleCredentials(LegacyIamRole.AwsGsaasGSOCOpsReadOnly);
  const onboardingValidationCreds = useCredentialsForPermissionClaim(PermissionClaim.CustomerOnboardingValidation);
  const onboardingMutatingCreds = useCredentialsForPermissionClaim(PermissionClaim.CustomerOnboardingMutating);

  const legacyClient = React.useMemo(() => {
    if (gsocReadonlyCredentials === null) return null;
    return new ControlPlaneClientManager({
      credentials: gsocReadonlyCredentials,
      gsocRegion: region,
      stage,
      username,
    });
  }, [gsocReadonlyCredentials, region, stage, username]);

  const onboardingValidationClient = React.useMemo(() => {
    if (onboardingValidationCreds === null || onboardingValidationCreds === NOT_LOADED) return null;
    return new ControlPlaneClientManager({
      credentials: onboardingValidationCreds,
      gsocRegion: region,
      stage,
      username,
      permission: PermissionClaim.CustomerOnboardingValidation,
    });
  }, [onboardingValidationCreds, region, stage, username]);

  const onboardingMutatingClient = React.useMemo(() => {
    if (onboardingMutatingCreds === null || onboardingMutatingCreds === NOT_LOADED) return null;
    return new ControlPlaneClientManager({
      credentials: onboardingMutatingCreds,
      gsocRegion: region,
      stage,
      username,
      permission: PermissionClaim.CustomerOnboardingMutating,
    });
  }, [onboardingMutatingCreds, region, stage, username]);

  const providedValue = React.useMemo(
    () => ({
      legacy: legacyClient,
      [PermissionClaim.CustomerOnboardingValidation]: onboardingValidationClient,
      [PermissionClaim.CustomerOnboardingMutating]: onboardingMutatingClient,
    }),
    [legacyClient, onboardingValidationClient, onboardingMutatingClient],
  );

  return (
    <ControlPlaneClientManagerContext.Provider value={providedValue}>
      {props.children}
    </ControlPlaneClientManagerContext.Provider>
  );
}
